/**
 * @fliisi/pollen — Tamagui Configuration
 *
 * The createTamagui() config that wires tokens, themes, fonts,
 * animations and media queries together. This is what the app
 * passes to <TamaguiProvider config={...} />.
 */
import { createTamagui, createMedia } from 'tamagui'
import { tokens } from './tokens'
import { themes } from './themes'
import { headingFont, bodyFont, cursiveFont } from './fonts'
import { animations } from './animations'

// ─── Media Queries / Breakpoints ─────────────────────────────────────────

const media = createMedia({
  mobile: { maxWidth: 767 },
  tablet: { minWidth: 768, maxWidth: 1023 },
  desktop: { minWidth: 1024 },
  // Shortcuts for "greater than" patterns
  gtMobile: { minWidth: 768 },
  gtTablet: { minWidth: 1024 },
  // Short-side breakpoints (for landscape detection)
  short: { maxHeight: 500 },
})

// ─── Config ──────────────────────────────────────────────────────────────

export const config = createTamagui({
  tokens,
  themes,
  media,
  animations,

  fonts: {
    heading: headingFont,
    body: bodyFont,
    cursive: cursiveFont,
  },

  // Default props applied to all Tamagui components
  defaultProps: {
    // Make all Text components use body font by default
  },

  settings: {
    // Allow inline styles as escape hatch (development flexibility)
    allowedStyleValues: 'somewhat-strict-web',
    // Autocomplete token values in IDE
    autocomplete: 'on',
  },

  // Shorthands — type-safe abbreviations for common props
  // Keep it small; only add shorthands that are truly universal
  shorthands: {
    // Layout
    p: 'padding',
    pt: 'paddingTop',
    pb: 'paddingBottom',
    pl: 'paddingLeft',
    pr: 'paddingRight',
    px: 'paddingHorizontal',
    py: 'paddingVertical',
    m: 'margin',
    mt: 'marginTop',
    mb: 'marginBottom',
    ml: 'marginLeft',
    mr: 'marginRight',
    mx: 'marginHorizontal',
    my: 'marginVertical',
    // Sizing
    w: 'width',
    h: 'height',
    // Appearance
    bg: 'backgroundColor',
    br: 'borderRadius',
    bw: 'borderWidth',
    bc: 'borderColor',
    // Flex
    f: 'flex',
    ai: 'alignItems',
    jc: 'justifyContent',
    fd: 'flexDirection',
    fw: 'flexWrap',
    fg: 'flexGrow',
    fs: 'flexShrink',
  } as const,
})

// ─── Type Export ────────────────────────────────────────────────────────
// This makes Tamagui's types aware of our tokens & themes globally.

export type AppConfig = typeof config

declare module 'tamagui' {
  interface TamaguiCustomConfig extends AppConfig {}
}
